import { FC, useEffect, useState } from "react";
import { TaskData, TaskListItem } from "../../models/task";
import { Avatar, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Menu, MenuButtonProps, MenuItem, MenuList, MenuPopover, MenuTrigger, Option, Spinner, SplitButton, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Textarea, Tooltip } from "@fluentui/react-components";
import { User, UserUpdateDTO } from "../../models/users";
import { getDocument, updateDocument } from "../../services/documentService";
import { CheckmarkRegular, CheckRegular, DismissRegular } from "@fluentui/react-icons";
import { updateTask } from "../../services/taskService";
import { getTenderDocument, updateTenderDocument } from "../../services/tenderingService";
import { Tender } from "../../models/tendering";
import { deepCloneObject } from "../../utils/clone";
import { useAppContext } from "../../AppContext";
import { createUserRoleAssignment, getRolesByEmail } from "../../services/roleService";
import { RoleDTO } from "../../models/role";
import { getUserById, updateUser } from "../../services/usersService";


type Props = {
  onClose: () => void;
  open: boolean;
  task: TaskListItem;
  users: User[];
  onTaskUpdated: (updatedTask: TaskListItem) => void;
};

const EditTaskDialog: FC<Props> = ({onClose, open, task, users, onTaskUpdated}) => {
  const { user } = useAppContext();

  const [taskData, setTaskData] = useState<TaskData | null>(null);
  const [updatedTask, setUpdatedTask] = useState<TaskListItem>(task);
  const [documentData, setDocumentData] = useState<any>(null);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [selectedRoleID, setSelectedRoleID] = useState<number>();
  const [issuedByUser, setIssuedByUser] = useState<User | null>(null);

  useEffect(() => {
    if (open) {
      setTaskData(JSON.parse(task.taskData as string) as TaskData);
      getUserById(task.taskIssuedByUserID)
        .then(response => setIssuedByUser(response.user))
        .catch(() => setIssuedByUser(null));
    }
    else{
      setTaskData(null);
      setDocumentData(null);
      setIssuedByUser(null);
    }
  }, [open]);

  useEffect(() => {
    const fetchDocument = async (documentID: string) => {
      try {
        const response = await getDocument(documentID);
        setDocumentData(response);
        console.log(documentData);
      } catch (error) {
        console.error('Error fetching document:', error);
      }
    };

    const fetchRoles = async (domain: string) => {
      try {
        const response = await getRolesByEmail(domain);
        setRoles(response);
      } catch (error) {
        console.error('Error fetching document:', error);
      }
    }

    if (taskData) {
      if(task.taskTypeID === 4){
        fetchDocument(taskData!.DocumentID);
      }
      if(task.taskTypeID === 1){
        fetchRoles(taskData!.Email.split('@')[1])
      };
    }

  }, [taskData]);

  const returnStatusText = (): string => {
    if (task.isTaskApproved && task.isTaskCompleted) {
      return 'Accepterad';
    }
    else if (task.isTaskApproved) {
      return 'Godkänt';
    }
    else if (task.isTaskApproved === false) {
      return 'Nekad';
    }
    else if (task.isTaskApproved === null) {
      return 'Väntar på svar';
    }
    return 'Okänd status';
  }

  const returnUserName = (userID: number): string => {
    const user = users.find(u => u.userID === userID);
    return user ? `${user.firstName} ${user.lastName}` : 'Okänd användare';
  }

  const onSaveTask = async (): Promise<void> => {
    try{
      await updateTask(task.taskID, updatedTask);
    }
    catch(error){
      console.error("Error updating task:", error);
    }
    finally{
      onTaskUpdated(updatedTask);
      onClose();
    }
  }

  const onDeny = async (): Promise<void> => {
    if(documentData && taskData){
      setDocumentData((prev: any) => ({...prev, archived: true}));
      const deniedTask = {...updatedTask, isTaskApproved: false};
      try{
        await updateDocument(taskData.DocumentID, {...documentData, archived: true });
        await updateTask(task.taskID, deniedTask);
      }
      catch(error){
        console.error("Error denying task:", error);
      }
      finally{
        setUpdatedTask(deniedTask);
        onTaskUpdated(deniedTask);
        onClose();
      }
    }
  }

  const onAccept = async (): Promise<void> => {
    console.log("Accepting task...");
    if(documentData && taskData && task.taskTypeID == 4){
      try{
          await updateDocument(taskData.DocumentID, {...documentData, archived: true }).then(() => {
          setUpdatedTask({...updatedTask, isTaskApproved: true});
        });
      }
      catch(error){
        console.error("Error archiving document:", error);
      }
      finally{
        await acceptTenderChanges();
      }
    }
    else if(task.taskTypeID == 6){
      try{
        const taskResult = await updateTask(task.taskID, {...updatedTask, isTaskApproved: true});
        const roleResult = await createUserRoleAssignment(taskData!.UserID, taskData!.RoleID, task.taskModuleID);
        onTaskUpdated({...updatedTask, isTaskApproved: true});
        onClose();
      }
      catch(error){
        console.error("Error accepting user creation task:", error);
      }
    }
    else{
      const user: UserUpdateDTO = {
        Email: taskData!.Email,
        FirstName: taskData!.FirstName,
        LastName: taskData!.LastName,
        RoleID: selectedRoleID ? +selectedRoleID : 0,
        Modules: task.taskModuleID ? [task.taskModuleID] : [],
        IsDisabled: false,
      };
      try{
        const userResult = await updateUser(taskData!.UserID, user);
        const taskResult = await updateTask(task.taskID, {...updatedTask, isTaskApproved: true});
        onTaskUpdated({...updatedTask, isTaskApproved: true});
        onClose();
      }
      catch(error){
        console.error("Error accepting user creation task:", error);
      }
    }
  }

  const onTaskOwnerSelect = (userID: string): void => {
    setUpdatedTask({...updatedTask, taskOwner: +userID});
  }

  const onCommentChange = (comment: string): void => {
    setUpdatedTask({...updatedTask, taskComment: comment});
  }

  const convertAnswerValue = (oldnew: string): string => {
    if(documentData[oldnew].type === 'bool'){
      return documentData[oldnew].value == 1 ? 'Ja' : 'Nej';
    }
    else if(documentData[oldnew].type === 'string'){
      return documentData[oldnew].value?.replace(/\n/g, '<br>');
    }
    else{
      return documentData[oldnew].value;
    }
  }

  const acceptTenderChanges = async (): Promise<void> => {
    try{
      await getTenderDocument(taskData!.TenderModuleID).then(async (tender: Tender) => {
        if(tender && tender.tenderAnswers){
          let curr = tender.tenderAnswers.find(old => old.questionID === documentData.new.questionID);
          
          const org = deepCloneObject(curr);
          let found;
          tender.tenderAnswers.forEach((element, index) => {
            if (element.questionID === documentData.new.questionID) {
              found = index;
            }
          });
          if (found) { 
            tender.tenderAnswers[found] = documentData.new;
          }
          else {
            tender.tenderAnswers.push(documentData.new);
          }
          
          if (tender.changes) {
            tender.changes.push({
              oldAnswer: curr,
              newAnswer: documentData.new, 
              date: Date.now(),
              taskIssuedByUser: task.taskIssuedByUserID, 
              taskApprovedByUser: user.user.userID});
          }
          else {
            tender.changes = [{oldAnswer: curr, newAnswer: documentData.new}];
          }
        }

        await updateTenderDocument(taskData!.TenderModuleID, tender).then(async () => {
          const taskResult = await updateTask(task.taskID, {...updatedTask, isTaskApproved: true});
          onTaskUpdated({...updatedTask, isTaskApproved: true});
          onClose();
        });
      });
    }
    catch(error){
      console.error("Error accepting tender changes:", error);
    }       
  }

  return (
    <Dialog 
      open={open} 
      onOpenChange={(_event, data) => !data.open && onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle            
          action={
            <Button
              appearance="subtle"
              shape="circular"
              aria-label="close"
              icon={<DismissRegular />}
              onClick={onClose}
            />
          }>
            <Tooltip content={`[${task.taskID}] ${task.taskTypeID == 4 ? 'Ändring av: ' : ''}${task.taskDescription}`} relationship="label">
              <span className="block truncate">[{task.taskID}] {task.taskTypeID == 4 ? "Ändring av:" : ""} {task.taskDescription}</span>
            </Tooltip>
          </DialogTitle>
          <DialogContent>
            <Text>Status: {returnStatusText()}</Text>
            {task.taskTypeID !== 6 && <div className="flex my-5 flex-col">
              <Text>Verksamhetens namn: {task.taskTypeID == 4 ? task.taskName : taskData?.DepartmentID}</Text>
              <Text>Begärd av: {task.taskTypeID == 4 ? (issuedByUser ? `${issuedByUser.firstName} ${issuedByUser.lastName}` : 'Laddar...') : taskData?.FirstName + " " + taskData?.LastName}</Text>
              <Text>Datum begärd: {new Date(task.taskCreated).toLocaleDateString('sv-SE')}</Text>
            </div>}
            {task.taskTypeID === 4 && <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell></TableHeaderCell>
                  <TableHeaderCell>
                    <Text weight="semibold">Svar</Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text weight="semibold">Kommentar</Text>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="even:bg-gray-50">
                  <TableCell>
                    Från:
                  </TableCell>
                  <TableCell>
                    <div className="max-h-36 overflow-y-auto">
                      {documentData ? <span dangerouslySetInnerHTML={{ __html: convertAnswerValue('old') }} /> : <Spinner />}
                    </div>
                  </TableCell>
                  <TableCell>
                    {documentData ? documentData.old.comment : <Spinner />}
                  </TableCell>
                </TableRow>
                <TableRow className="even:bg-gray-50">
                  <TableCell>
                    Till:
                  </TableCell>
                  <TableCell>
                    <div className="max-h-36 overflow-y-auto">
                      {documentData ? <span dangerouslySetInnerHTML={{ __html: convertAnswerValue('new') }} /> : <Spinner />}
                    </div>
                  </TableCell>
                  <TableCell>
                    {documentData ? documentData.new.comment : <Spinner />}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>}
            {task.taskTypeID === 4 && (task.isTaskApproved == null) && <div className="flex flex-col my-5">
              <Field label="Välj ansvarig">
                <Dropdown
                  positioning={{ autoSize: false }}
                  listbox={{
                    style: {
                      maxHeight: '300px',
                      overflowY: 'auto'
                    },
                  }}
                  value={updatedTask.taskOwner ? returnUserName(updatedTask.taskOwner) : undefined}
                  onOptionSelect={(_, data) => {
                    if (data.optionValue !== undefined) {
                      onTaskOwnerSelect(data.optionValue);
                    }
                  } }
                >
                  {users.map(user => (
                    <Option
                      key={user.userID}
                      text={`${user.firstName} ${user.lastName}`}
                      value={user.userID.toString()}
                    >
                      <Avatar name={`${user.firstName} ${user.lastName}`} /> {user.firstName} {user.lastName}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Kommentar">
                <Textarea value={updatedTask.taskComment || ''} onChange={(e) => onCommentChange(e.target.value)} resize="vertical" />
              </Field>
            </div>}
            {task.taskTypeID === 1  && (task.isTaskApproved == null) && (
              <Field label="Välj roll">
                <Dropdown
                  onOptionSelect={(_, data) => {
                    if (data.optionValue !== undefined) {
                      setSelectedRoleID(+data.optionValue);
                    }
                  }}
                >
                  {roles.map(role => (
                    <Option
                      key={role.roleID}
                      text={role.roleName}
                      value={role.roleID.toString()}
                    >
                      {role.roleName}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} size="large">Stäng</Button>
            {(task.isTaskApproved == null) && <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                {(triggerProps: MenuButtonProps) => (
                  <SplitButton
                    size="large"
                    appearance="primary"
                    menuButton={triggerProps}
                    primaryActionButton={{ onClick: () => onSaveTask() }}
                  >
                    Spara
                  </SplitButton>
                )}
              </MenuTrigger>
              
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={onAccept} icon={<CheckmarkRegular />}>Acceptera</MenuItem>
                  <MenuItem onClick={onDeny} icon={<DismissRegular />}>Neka</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default EditTaskDialog;